// Shared full-settlement logic — used by:
//  - OdemeDengesi.jsx (Section 4 transfer list)
//  - Dashboard.jsx (per-cabin ORTAK HESAP DENGELEME)
// Tekne EUR ödemeleri kabin-arası settlement'a dahil DEĞİL (her kabin kendi payını
// üçüncü tarafa öder). Sadece: alışveriş kişi-başı payı + tekne diğer kişi-başı payı
// + offset (İsmet-credit convention: c1 alacaklı, diğer kabinlerin pozitif offset_try'ı borç).

export function computeFullSettlement({ cabins = [], payments = [], actuals = [], shoppingItems = [], settings = {}, digerSummary = {} , itemTotal = null }) {
  const empty = { rows: [], transfers: [] };
  if (!cabins.length) return empty;
  const rate = Number(settings.eur_try_rate) || 55.32;
  const parsePct = (v) => {
    if (v === undefined || v === null || v === "") return 100;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 100;
  };
  const shopPct = parsePct(settings.realization_shop_pct) / 100;
  const digerPct = parsePct(settings.realization_diger_pct) / 100;
  const totalKisi = cabins.reduce((s, c) => s + (c.people?.length || 0), 0);
  const _defaultItemTotal = (i) => {
    const qty = Number(i.qty || 0);
    const price = Number(i.unit_price_try ?? i.unit_price ?? 0);
    return qty * price;
  };
  const iTotal = itemTotal || _defaultItemTotal;
  const menuErzakTotal = (shoppingItems || []).reduce((s, i) => s + iTotal(i), 0);
  const digerTarget = digerSummary.target_try || 0;
  // Settlement için "grup toplamı" = MAX(tahmini × pct, gerçek ödenen). Böylece:
  //   - Trip henüz tamamlanmamışsa (paid < estimate×pct) tahmin baz alınır.
  //   - Ancak birisi tahminden fazla ödediyse (bizim örnek: 65513 alışveriş) o miktar baz olur.
  const shopPaidTotal = (payments || []).reduce((s, p) => (p.payment_type === "alisveris" ? s + (p.amount_try || 0) : s), 0)
    + (actuals || []).reduce((s, a) => s + (a.amount_try || 0), 0);
  const digerPaidTotal = (payments || []).reduce((s, p) => (p.payment_type === "diger_maliyet" ? s + (p.amount_try || 0) : s), 0);
  // Section 2 (shop) grup toplamı: kullanıcı formülü paid + estimate * (1 - pct/100)
  // Section 3 (diger) grup toplamı: Section 3 UI ile aynı: max(target * pct/100, paid)
  const shopGroupTotal = menuErzakTotal * (1 - shopPct) + shopPaidTotal;
  const digerGroupTotal = Math.max(digerTarget * digerPct, digerPaidTotal);
  // Lookups
  const u2c = {};
  const name2c = {};
  cabins.forEach((c) => {
    (c.occupants || []).forEach((u) => { u2c[u] = c.id; });
    (c.people || []).forEach((p) => {
      const fn = (p.full_name || "").trim();
      if (fn) name2c[fn] = c.id;
    });
  });
  // İsmet (c1) offset credit convention — offset'ler AYRI listede tutulur
  const ismetCabin = cabins.find((c) => c.id === "c1") || cabins[0];
  const ismetId = ismetCabin ? ismetCabin.id : "";
  const cabinById = {};
  cabins.forEach((c) => { cabinById[c.id] = c; });
  const offsetTransfers = [];
  cabins.forEach((c) => {
    if (c.id === ismetId) return;
    const off = Number(c.offset_try || 0);
    if (off > 0.5) {
      offsetTransfers.push({
        from_cabin_id: c.id, from_name: c.name,
        to_cabin_id: ismetId, to_name: ismetCabin?.name || "İsmet",
        amount_try: off,
      });
    }
  });
  // Per-cabin fair + paid — Ayrı Section 2 (shop) ve Section 3 (diger) toplamları
  const base = cabins.map((c) => {
    const persons = c.people?.length || 0;
    const shopShare = totalKisi > 0 ? shopGroupTotal * (persons / totalKisi) : 0;
    const digerShare = totalKisi > 0 ? digerGroupTotal * (persons / totalKisi) : 0;
    const fairShare = shopShare + digerShare;
    let shopPaid = 0;
    let digerPaid = 0;
    (payments || []).forEach((p) => {
      const ptype = p.payment_type || "gemi";
      if (ptype === "gemi") return;
      const payerCabin = p.cabin_id || name2c[p.person_label || ""] || "";
      let attribution = 0;
      if (p.for_all) {
        const amt = (p.amount_try || 0) + (p.amount_eur || 0) * rate;
        const payerLabel = (p.person_label || "").trim();
        if (payerLabel) {
          const pc = name2c[payerLabel] || "";
          if (pc === c.id) attribution = amt;
        } else if (totalKisi > 0) {
          attribution = amt * (persons / totalKisi);
        }
      } else if (payerCabin === c.id) {
        attribution = (p.amount_try || 0) + (p.amount_eur || 0) * rate;
      }
      if (attribution > 0) {
        if (ptype === "alisveris") shopPaid += attribution;
        else if (ptype === "diger_maliyet") digerPaid += attribution;
      }
    });
    (actuals || []).forEach((a) => { if (u2c[a.payer_username || ""] === c.id) shopPaid += (a.amount_try || 0); });
    const paid = shopPaid + digerPaid;
    return { id: c.id, name: c.name, order: c.order, fairShare, paid, shopShare, digerShare, shopPaid, digerPaid };
  });
  // Section 2 (shop) balances per cabin
  const rows = base.map((r) => ({ ...r, expected: r.fairShare, net: r.paid - r.fairShare, shopBalance: r.shopPaid - r.shopShare, digerBalance: r.digerPaid - r.digerShare }));

  // Absorbe modeli (yeni basit): offsetler İsmet'in PAY'ını (share) azaltır ve
  // absorber'ın PAY'ını artırır. Sırayla önce Sec 2, sonra Sec 3.
  // Ödenen (paid) sütununa dokunulmaz. Section 4 = min-transfer(adjusted combined) + offset residual.
  const ismetRow = rows.find((r) => r.id === ismetId) || null;
  const orderedOffsets = [...offsetTransfers].sort((a, b) => {
    const ao = cabinById[a.from_cabin_id]?.order ?? 0;
    const bo = cabinById[b.from_cabin_id]?.order ?? 0;
    return ao - bo;
  });
  const shopShareAdj = {};
  const digerShareAdj = {};
  const adjustedOffsets = [];
  let remIsmetShopShare = ismetRow?.shopShare || 0;
  let remIsmetDigerShare = ismetRow?.digerShare || 0;
  for (const o of orderedOffsets) {
    let rem = o.amount_try;
    // Sec 2 absorbe
    if (remIsmetShopShare > 0.5) {
      const a = Math.min(rem, remIsmetShopShare);
      remIsmetShopShare -= a;
      shopShareAdj[ismetId] = (shopShareAdj[ismetId] || 0) - a;
      shopShareAdj[o.from_cabin_id] = (shopShareAdj[o.from_cabin_id] || 0) + a;
      rem -= a;
    }
    // Sec 3 absorbe
    if (rem > 0.5 && remIsmetDigerShare > 0.5) {
      const a = Math.min(rem, remIsmetDigerShare);
      remIsmetDigerShare -= a;
      digerShareAdj[ismetId] = (digerShareAdj[ismetId] || 0) - a;
      digerShareAdj[o.from_cabin_id] = (digerShareAdj[o.from_cabin_id] || 0) + a;
      rem -= a;
    }
    // Residual → İsmet cabin-cabin
    if (rem > 0.5) {
      adjustedOffsets.push({ ...o, amount_try: rem });
    }
  }

  // Section 4: min-transfer(adjusted combined) + adjusted offset residuals
  const combined = rows.map((r) => ({
    id: r.id,
    name: r.name,
    bal: (r.shopPaid - (r.shopShare + (shopShareAdj[r.id] || 0))) + (r.digerPaid - (r.digerShare + (digerShareAdj[r.id] || 0))),
  }));
  const debtors = combined.filter((x) => x.bal < -0.5).map((x) => ({ ...x })).sort((a, b) => a.bal - b.bal);
  const creditors = combined.filter((x) => x.bal > 0.5).map((x) => ({ ...x })).sort((a, b) => b.bal - a.bal);
  const shopTransfers = [];
  const transfers = [];
  let di = 0, ci = 0;
  while (di < debtors.length && ci < creditors.length) {
    const d = debtors[di], cr = creditors[ci];
    const amt = Math.min(-d.bal, cr.bal);
    if (amt > 0.5) {
      const tx = { from_cabin_id: d.id, from_name: d.name, to_cabin_id: cr.id, to_name: cr.name, amount_try: amt };
      shopTransfers.push(tx);
      transfers.push(tx);
      d.bal += amt; cr.bal -= amt;
    }
    if (Math.abs(d.bal) < 0.5) di++;
    if (Math.abs(cr.bal) < 0.5) ci++;
  }
  adjustedOffsets.forEach((o) => {
    transfers.push({
      from_cabin_id: o.from_cabin_id, from_name: o.from_name,
      to_cabin_id: o.to_cabin_id, to_name: o.to_name,
      amount_try: o.amount_try,
    });
  });
  // Aynı (from_cabin_id → to_cabin_id) transferleri tek satırda topla
  // (örn. min-transfer 27.000 + offset residual 5.882 = tek satır 32.882)
  const mergedMap = new Map();
  transfers.forEach((t) => {
    const key = `${t.from_cabin_id}__${t.to_cabin_id}`;
    if (mergedMap.has(key)) {
      mergedMap.get(key).amount_try += t.amount_try;
    } else {
      mergedMap.set(key, { ...t });
    }
  });
  const mergedTransfers = [...mergedMap.values()].sort((a, b) => b.amount_try - a.amount_try);
  return { rows, transfers: mergedTransfers, shopShareAdj, digerShareAdj, offsetTransfers, shopTransfers, adjustedOffsets };
}
